import {
  MentionChartData,
  ChartDataSource,
  SeriesDataPoint,
} from "~/types/ChartsType";
import { SavedOrNestedSearches } from "~/types/SearchStateType";
import { SOCIAL_MEDIA_TYPES } from "~/constants/social";

export type Series = {
  name: string;
  type: string;
  data: SeriesDataPoint[];
  color?: string;
};

export type PieSeriesItem = {
  name: string;
  y: number;
  color?: string;
};

type ProfileData = {
  id: string;
  type: string;
};

interface DateValue {
  date: string;
  total?: number;
}

interface ArticleSentiment extends DateValue {
  positive: number;
  neutral: number;
  negative: number;
}

interface MediaTypeData {
  articles: DateValue[];
  article_sentiment: ArticleSentiment[];
  social: DateValue[];
  reach: DateValue[];
  ave: DateValue[];
}

interface CurrentMediaTypeData {
  [mediaType: string]: MediaTypeData;
}

export class SeriesProcessor {
  private profilesChartsData: Array<MentionChartData>;
  private nestedSearches: SavedOrNestedSearches;
  private selectedNestedSearchId: string;

  constructor(
    profilesChartsData: Array<MentionChartData>,
    nestedSearches: SavedOrNestedSearches,
    selectedNestedSearchId: string,
  ) {
    this.profilesChartsData = profilesChartsData;
    this.nestedSearches = nestedSearches;
    this.selectedNestedSearchId = selectedNestedSearchId;
  }

  private getProfileName(profileId: string): string {
    const matchingProfile = this.nestedSearches.find(
      (search) => search.id === profileId,
    );
    return matchingProfile?.attributes.name || "";
  }

  private extractProfileId(
    profileData: Array<ProfileData> | ProfileData,
  ): string | null {
    return Array.isArray(profileData) ? profileData[0]?.id : profileData?.id;
  }

  private processTimeSeriesData(data: SeriesDataPoint[]): SeriesDataPoint[] {
    return data.map((point) => {
      if (typeof point === "object" && !Array.isArray(point)) {
        const { total, date } = point;
        return [
          Date.parse(date as string),
          typeof total === "number" ? total : 0,
        ];
      }
      return point;
    });
  }

  processReachSeries(type: string, sliceBy: string): Series[] {
    return this.processProfileSeries(type, {
      dataProcessor:
        sliceBy !== "media_type"
          ? this.processTimeSeriesData
          : this.processReachByMediaType.bind(this),
      sliceBy,
      source: "reach",
    });
  }

  processAmplificationSeries(type: string, sliceBy: string): Series[] {
    return this.processProfileSeries(type, {
      dataProcessor:
        sliceBy === "media_type"
          ? this.processAmplificationByMediaType.bind(this)
          : this.processTimeSeriesData,
      sliceBy,
      source: "social",
    });
  }

  processAmplificationByMediaType(
    currentMediaTypeData: CurrentMediaTypeData,
  ): PieSeriesItem[] {
    return this.processMediaTypePieData(currentMediaTypeData, "social");
  }

  processAmplificationPieSeries(): PieSeriesItem[] {
    let pieSeriesData: PieSeriesItem[] = [];

    this.profilesChartsData.forEach((profileChartsData) => {
      const profileData = profileChartsData?.relationships?.profile?.data;
      if (!profileData) return pieSeriesData;
      const profileId = this.extractProfileId(profileData);

      if (profileId) {
        const assignSeriesData = ["-1", String(profileId)].includes(
          String(this.selectedNestedSearchId),
        );
        if (assignSeriesData) {
          pieSeriesData = profileChartsData?.attributes?.search_result
            ?.counts_by_media_type
            ? this.processAmplificationByMediaType(
                profileChartsData.attributes.search_result.counts_by_media_type,
              )
            : [];
        }
      }
    });
    return pieSeriesData;
  }

  processReachPieSeries(sliceBy: string): PieSeriesItem[] {
    let pieSeriesData: PieSeriesItem[] = [];

    this.profilesChartsData.forEach((profileChartsData) => {
      const profileData = profileChartsData?.relationships?.profile?.data;
      if (!profileData) return pieSeriesData;
      const profileId = this.extractProfileId(profileData);

      if (profileId) {
        // Only grab the data for the selected nested/saved search.
        // OR if for some reason there's no search selected,
        // just dump whatever we have in there to not break the chart.
        const assignSeriesData = ["-1", String(profileId)].includes(
          String(this.selectedNestedSearchId),
        );
        switch (sliceBy) {
          case "media_type":
            if (assignSeriesData) {
              pieSeriesData = profileChartsData?.attributes?.search_result
                ?.counts_by_media_type
                ? this.processReachByMediaType(
                    profileChartsData.attributes.search_result
                      .counts_by_media_type,
                  )
                : [];
            }
            break;
        }
      }
    });

    return pieSeriesData;
  }

  processMentionsBarSeries(type: string, sliceBy: string): Series[] {
    return this.processProfileSeries(type, {
      dataProcessor:
        sliceBy !== "media_type"
          ? this.processTimeSeriesData
          : this.processMentionsByMediaType.bind(this),
      sliceBy,
      source: "articles",
    });
  }

  processMentionsColumnSeries(type: string, sliceBy: string): Series[] {
    return this.processProfileSeries(type, {
      dataProcessor:
        sliceBy !== "media_type"
          ? this.processTimeSeriesData
          : this.processMentionsByMediaType.bind(this),
      sliceBy,
      source: "articles",
    });
  }

  processMentionsLineSeries(type: string, sliceBy: string): Series[] {
    return this.processProfileSeries(type, {
      dataProcessor: this.processTimeSeriesData,
      sliceBy,
      source: "articles",
    });
  }

  processMentionsPieSeries(sliceBy: string): PieSeriesItem[] {
    let pieSeriesData: PieSeriesItem[] = [];

    this.profilesChartsData.forEach((profileChartsData) => {
      const profileData = profileChartsData?.relationships?.profile?.data;
      if (!profileData) return pieSeriesData;
      const profileId = this.extractProfileId(profileData);

      if (profileId) {
        // Only grab the data for the selected nested/saved search.
        // OR if for some reason there's no search selected,
        // just dump whatever we have in there to not break the chart.
        const assignSeriesData = ["-1", String(profileId)].includes(
          String(this.selectedNestedSearchId),
        );
        switch (sliceBy) {
          case "media_type":
            if (assignSeriesData) {
              pieSeriesData = profileChartsData?.attributes?.search_result
                ?.counts_by_media_type
                ? this.processMentionsByMediaType(
                    profileChartsData.attributes.search_result
                      .counts_by_media_type,
                  )
                : [];
            }
            break;
          case "snapshot":
            if (assignSeriesData) {
              pieSeriesData = profileChartsData?.attributes?.search_result
                ?.counts.article_sentiment
                ? this.processMentionsBySentiment(
                    profileChartsData.attributes.search_result.counts
                      .article_sentiment,
                  )
                : [];
            }
            break;
        }
      }
    });

    return pieSeriesData;
  }

  processSentimentOverTime(type: string): Series[] {
    let seriesData: Series[] = [];

    this.profilesChartsData.forEach((profileChartsData) => {
      const profileData = profileChartsData?.relationships?.profile?.data;
      if (!profileData) return seriesData;
      const profileId = this.extractProfileId(profileData);

      if (profileId) {
        const assignSeriesData = ["-1", String(profileId)].includes(
          String(this.selectedNestedSearchId),
        );

        if (assignSeriesData) {
          seriesData = profileChartsData?.attributes?.search_result?.counts
            .article_sentiment
            ? this.processSentimentSeriesOverTime(
                type,
                this.processTimeSeriesData,
                profileChartsData.attributes.search_result.counts
                  .article_sentiment,
              )
            : [];
        }
      }
    });

    return seriesData;
  }

  processCompetitorsPieSeries(metric: string): PieSeriesItem[] {
    return this.profilesChartsData.reduce(
      (pieSeriesData, profileChartsData) => {
        const profileData = profileChartsData?.relationships?.profile?.data;
        if (!profileData) return pieSeriesData;
        const profileId = this.extractProfileId(profileData);

        if (profileId) {
          const profileName = this.getProfileName(profileId);
          let counts = 0;

          switch (metric) {
            case "mentions":
              counts = profileChartsData?.attributes?.search_result?.total || 0;
              break;
            case "reach":
              counts = (
                profileChartsData?.attributes?.search_result?.counts.reach || []
              ).reduce((sum, { total }) => sum + total, 0);
              break;
            case "amplification":
              counts = (
                profileChartsData?.attributes?.search_result?.counts.social ||
                []
              ).reduce((sum, { total }) => sum + total, 0);
              break;
            case "ave":
              counts = (
                profileChartsData?.attributes?.search_result?.counts.ave || []
              ).reduce((sum, { total }) => sum + total, 0);
              break;
          }

          pieSeriesData.push({ name: profileName, y: counts });
        }

        return pieSeriesData;
      },
      [] as PieSeriesItem[],
    );
  }

  processAveSeries(type: string, sliceBy: string): Series[] {
    return this.processProfileSeries(type, {
      dataProcessor:
        sliceBy !== "media_type"
          ? this.processTimeSeriesData
          : this.processAveByMediaType.bind(this),
      sliceBy,
      source: "ave",
    });
  }

  processAvePieSeries(sliceBy: string): PieSeriesItem[] {
    let pieSeriesData: PieSeriesItem[] = [];

    this.profilesChartsData.forEach((profileChartsData) => {
      const profileData = profileChartsData?.relationships?.profile?.data;
      if (!profileData) return pieSeriesData;
      const profileId = this.extractProfileId(profileData);

      if (profileId) {
        // Only grab the data for the selected nested/saved search.
        // OR if for some reason there's no search selected,
        // just dump whatever we have in there to not break the chart.
        const assignSeriesData = ["-1", String(profileId)].includes(
          String(this.selectedNestedSearchId),
        );
        switch (sliceBy) {
          case "media_type":
            if (assignSeriesData) {
              pieSeriesData = profileChartsData?.attributes?.search_result
                ?.counts_by_media_type
                ? this.processAveByMediaType(
                    profileChartsData.attributes.search_result
                      .counts_by_media_type,
                  )
                : [];
            }
            break;
        }
      }
    });

    return pieSeriesData;
  }

  private processProfileSeries(
    type: string,
    options: {
      dataProcessor: Function;
      sliceBy: string;
      source: ChartDataSource;
    },
  ): Series[] {
    return this.profilesChartsData.reduce((series, profileChartsData) => {
      const profileData = profileChartsData?.relationships?.profile?.data;
      if (!profileData) return series;
      const profileId = this.extractProfileId(profileData);

      if (profileId) {
        const profileName = this.getProfileName(profileId);

        // Use a type guard to handle different data sources
        const searchResultCounts =
          profileChartsData?.attributes?.search_result?.counts;
        let currentProfileData: SeriesDataPoint[] | CurrentMediaTypeData = [];

        if (searchResultCounts) {
          switch (options.source) {
            case "articles":
              // media_type uses a different set of counts
              if (options.sliceBy === "media_type") {
                currentProfileData =
                  profileChartsData?.attributes?.search_result
                    ?.counts_by_media_type || {};
              } else {
                currentProfileData = searchResultCounts.articles || [];
              }
              break;
            case "reach":
              if (options.sliceBy === "time") {
                currentProfileData = searchResultCounts.reach || [];
              } else if (options.sliceBy === "media_type") {
                currentProfileData =
                  profileChartsData?.attributes?.search_result
                    ?.counts_by_media_type || [];
              } else {
                currentProfileData = (searchResultCounts.reach || []).map(
                  (item) => [0, item.total] as SeriesDataPoint,
                );
              }
              break;
            case "social":
              if (options.sliceBy === "media_type") {
                currentProfileData =
                  profileChartsData?.attributes?.search_result
                    ?.counts_by_media_type || {};
              } else if (options.sliceBy === "time") {
                currentProfileData = searchResultCounts.social || [];
              } else {
                currentProfileData = (searchResultCounts.social || []).map(
                  (item) => [0, item.total] as SeriesDataPoint,
                );
              }
              break;
            case "ave":
              if (options.sliceBy === "time") {
                currentProfileData = searchResultCounts.ave || [];
              } else if (options.sliceBy === "media_type") {
                currentProfileData =
                  profileChartsData?.attributes?.search_result
                    ?.counts_by_media_type || [];
              } else {
                currentProfileData = (searchResultCounts.ave || []).map(
                  (item) => [0, item.total] as SeriesDataPoint,
                );
              }
              break;
            default:
              currentProfileData = [];
          }
        }

        series.push({
          name: profileName,
          type,
          data: options.dataProcessor(currentProfileData),
        });
      }

      return series;
    }, [] as Series[]);
  }

  private processMediaTypePieData(
    currentMediaTypeData: CurrentMediaTypeData,
    dataSourceKey: keyof MediaTypeData,
  ): PieSeriesItem[] {
    const socialPlatforms = SOCIAL_MEDIA_TYPES;
    const finalSeries: PieSeriesItem[] = [];
    let combinedSocialTotal = 0;

    Object.entries(currentMediaTypeData).forEach(
      ([mediaTypeKey, mediaType]: [string, MediaTypeData]) => {
        const dataSource = mediaType[dataSourceKey] as DateValue[];
        const total =
          dataSource?.reduce(
            (sum: number, curr: DateValue) => sum + (curr?.total || 0),
            0,
          ) || 0;

        if (socialPlatforms.includes(mediaTypeKey.toLowerCase() as any)) {
          combinedSocialTotal += total;
        } else {
          finalSeries.push({
            name: mediaTypeKey,
            y: total,
          });
        }
      },
    );

    if (combinedSocialTotal > 0) {
      finalSeries.push({
        name: "social",
        y: combinedSocialTotal,
      });
    }
    return finalSeries;
  }

  private processAveByMediaType(
    currentMediaTypeData: CurrentMediaTypeData,
  ): PieSeriesItem[] {
    return this.processMediaTypePieData(currentMediaTypeData, "ave");
  }

  private processMentionsByMediaType(
    currentMediaTypeData: CurrentMediaTypeData,
  ): PieSeriesItem[] {
    return this.processMediaTypePieData(currentMediaTypeData, "articles");
  }

  private processReachByMediaType(
    currentMediaTypeData: CurrentMediaTypeData,
  ): PieSeriesItem[] {
    return this.processMediaTypePieData(currentMediaTypeData, "reach");
  }

  private processMentionsBySentiment(
    currentProfileSentiments: Array<ArticleSentiment>,
  ): PieSeriesItem[] {
    const totalSentiment = { negative: 0, positive: 0, neutral: 0 };

    currentProfileSentiments.forEach((sentiment) => {
      totalSentiment.positive += sentiment.positive;
      totalSentiment.negative += sentiment.negative;
      totalSentiment.neutral += sentiment.neutral;
    });

    return [
      { name: "Positive", y: totalSentiment.positive, color: "#10B981" },
      { name: "Neutral", y: totalSentiment.neutral, color: "#94A3B8" },
      { name: "Negative", y: totalSentiment.negative, color: "#DC2626" },
    ];
  }

  private processSentimentSeriesOverTime(
    type: string,
    dataProcessor: Function,
    currentProfileSentiments: Array<ArticleSentiment>,
  ): Series[] {
    const positiveSentiment = currentProfileSentiments.map((item) => ({
      total: item.positive,
      date: item.date,
    }));

    const neutralSentiment = currentProfileSentiments.map((item) => ({
      total: item.neutral,
      date: item.date,
    }));

    const negativeSentiment = currentProfileSentiments.map((item) => ({
      total: item.negative,
      date: item.date,
    }));

    return [
      {
        name: "Positive",
        type,
        data: dataProcessor(positiveSentiment),
        color: "#10B981",
      },
      {
        name: "Neutral",
        type,
        data: dataProcessor(neutralSentiment),
        color: "#94A3B8",
      },
      {
        name: "Negative",
        type,
        data: dataProcessor(negativeSentiment),
        color: "#DC2626",
      },
    ];
  }
}
